// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@adl/App/Components/Notify";
import { CustomAppState } from "@adl/App/Store/Reducers";
import {
    getReorderPad,
    getReorderPadIsLoading,
    getReorderPadProducts,
} from "@adl/App/Store/Selectors/ReorderPadSelectors";
import ReorderPadGrid from "@adl/App/Widgets/ReorderPad/ReorderPadGrid";
import ReorderPadTable from "@adl/App/Widgets/ReorderPad/ReorderPadTable";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

interface ReorderPadProductListStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

const reorderPadProductListStyles: ReorderPadProductListStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    container: {
        css: css`
            margin-top: 22px;
        `,
    },
};

const styles = reorderPadProductListStyles;
const ReorderPadProductList: FC<Props> = () => {
    const { products, failedMessage, isLoading, view, isTabletScreen } = useAppSelector(state => {
        return {
            products: getReorderPadProducts(state),
            isLoading: getReorderPadIsLoading(state),
            failedMessage: getReorderPad(state).failedMessage,
            view: getReorderPad(state).view,
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        };
    });

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!products || products.length === 0) {
        return (
            <StyledWrapper {...styles.container}>
                <Notify type={failedMessage ? "danger" : "info"}>{failedMessage || translate("No Orders.")}</Notify>
            </StyledWrapper>
        );
    }

    if (isTabletScreen) {
        return (
            <StyledWrapper {...styles.container} {...{ isTabletScreen }}>
                <ReorderPadGrid products={products} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container}>
            {view === "Table" ? <ReorderPadTable products={products} /> : <ReorderPadGrid products={products} />}
        </StyledWrapper>
    );
};

export default ReorderPadProductList;
